<template>
	<div
		ref="bait"
		class="jw-ad ad-banner pub_300x250 pub_300x250m pub_728x90 text-ad textAd text_ad text_ads text-ads text-ad-links"
		style="height: 1px; width: 1px; position: absolute"
	>
		<img
			ref="img"
			src="https://pagead2.googlesyndication.com/pagead/imgad?id=CICAgKDnqzjtABABGAEyCK1i2aHnQwMB"
			width="1"
			height="1"
			style="display: none"
			@error="() => (blocked = true)"
			@load="() => (blocked = false)"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, useTemplateRef } from 'vue';
import { SnowplowLoginContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';

const { jwLoginId } = storeToRefs(useUserStore());
const { titleContext } = storeToRefs(useTitleDetailsStore());
const { setSessionEnvironmentByKey } = useTrackingStore();
const { setIsUsingAdBlocker } = useAppStore();

const baitEl = useTemplateRef('bait');
const imgEl = useTemplateRef('img');

let handled = false;

const blocked = ref(false);
const lastTrackedValue = ref<boolean | null>(null);

function waitForBaitResult(maxMs = 300) {
	const el = imgEl.value;
	if (!el || blocked.value) return Promise.resolve();

	// SSR: it may have settled before hydration attached the listeners
	if (el.complete) {
		blocked.value = el.naturalWidth === 0;
		return Promise.resolve();
	}

	return new Promise<void>(resolve => {
		el.addEventListener('load', () => resolve(), { once: true });
		el.addEventListener('error', () => resolve(), { once: true });
		setTimeout(resolve, maxMs);
	});
}

function publish() {
	// already tracked an adblocker
	if (lastTrackedValue.value) return;

	lastTrackedValue.value = blocked.value;
	setIsUsingAdBlocker(!!blocked.value);
	setSessionEnvironmentByKey({ key: 'hasAdBlocker', value: blocked.value });
	setSessionEnvironmentByKey({ key: 'sessionReady', value: true });

	try {
		const contexts = [];
		if (titleContext.value) {
			const { titleId, objectType, seasonNumber, episodeNumber, contentType } = titleContext.value;
			contexts.push(
				new SnowplowTitleContextGraphql(titleId, objectType, seasonNumber, episodeNumber, contentType)
			);
		}

		if (jwLoginId.value) contexts.push(new SnowplowLoginContext(jwLoginId.value));

		Promise.resolve(
			TrackingHelper.trackEvent(
				'adblocker',
				{ action: 'detected', value: blocked.value ? 1 : 0, nonInteraction: true },
				contexts
			)
		).catch(() => {});
	} catch {
		// Tracking failures must never block ad requests
	}
}

async function handleAdblockerTracking() {
	if (handled) return;
	handled = true;

	await waitForBaitResult();
	publish();
}

// Rare late flip (error arrives after the cap): correct the value once
watch(blocked, isBlocked => {
	if (isBlocked && handled && lastTrackedValue.value === false) publish();
});

onMounted(() => {
	blocked.value = blocked.value || baitEl.value == null || window.getComputedStyle(baitEl.value).display === 'none';
	handleAdblockerTracking();
});
</script>
